import React, { useEffect, useState } from "react";
import "../../../styles/clinic-information.css";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useClinicData } from "../../../hooks/useClinicData";
const ClinicSidebar = ({
  selectedCard,
  setSelectedCard,
  isVeterinarian,
  index,
  tabType,
  hasClinicId,
  isCollapsed = false,
  setIsMobileMenuOpen,
  onShowClinicSelect,
}) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const params = useParams();
  const { getClinicId, getOwnedClinics, getClinicInfo, data, loading, error, refresh, isOwner } = useClinicData();
  const currentClinicId = getClinicId();
  const urlClinicId = params?.clinicId ? parseInt(params.clinicId) : null;
  const currentIndex = params?.index || 'setup';
  const [clinics, setClinics] = useState([]);
  const [isSwitchingClinic, setIsSwitchingClinic] = useState(false);
  // Use URL clinicId as source of truth, fallback to currentClinicId
  const selectedClinicId = urlClinicId || currentClinicId;

  // Track when clinic switching is complete (data loaded and URL matches)
  useEffect(() => {
    if (isSwitchingClinic) {
      // If loading is complete and we have matching data, or if there's an error, stop loading
      if (!loading && urlClinicId && (data?.clinic?.id === urlClinicId || error)) {
        setIsSwitchingClinic(false);
      }
    }
  }, [isSwitchingClinic, loading, urlClinicId, data?.clinic?.id, error]);

  // Get current clinic status
  const getCurrentClinicStatus = () => {
    // First try to get from clinic data directly (most reliable)
    const clinicData = data?.clinic;
    if (clinicData) {
      return clinicData.status || clinicData.verification_status || 'unverified';
    }
    // Fallback to clinic info from hook
    const clinicInfo = getClinicInfo();
    if (clinicInfo) {
      return clinicInfo.status || clinicInfo.verification_status || 'unverified';
    }
    // Try to get from selected clinic in clinics array (owned_clinics might have status)
    const selectedClinic = clinics.find(c => c.id === selectedClinicId || c.id === parseInt(selectedClinicId));
    if (selectedClinic && (selectedClinic.status || selectedClinic.verification_status)) {
      return selectedClinic.status || selectedClinic.verification_status;
    }
    // If still loading and no status found, check localStorage for cached status
    if (loading && selectedClinicId) {
      try {
        const cachedClinics = localStorage.getItem('owned_clinics');
        if (cachedClinics && cachedClinics !== 'undefined' && cachedClinics !== 'null') {
          const parsed = JSON.parse(cachedClinics);
          if (Array.isArray(parsed)) {
            const cachedClinic = parsed.find(c => c.id === selectedClinicId || c.id === parseInt(selectedClinicId));
            if (cachedClinic && (cachedClinic.status || cachedClinic.verification_status)) {
              return cachedClinic.status || cachedClinic.verification_status;
            }
          }
        }
      } catch (error) {
        console.error('Error reading cached clinic status:', error);
      }
    }
    // Default to 'unverified' if no status found (ensures unverified clinics are disabled)
    return 'unverified';
  };

  const isClinicVerified = () => {
    const status = getCurrentClinicStatus();
    return status === 'verified';
  };
  // Load owned clinics from localStorage on mount
  useEffect(() => {
    if (isOwner() || isVeterinarian) {
      try {
        // Try to get from useClinicData hook first (which reads from data or localStorage)
        const ownedClinics = getOwnedClinics();
        let clinicsList = [];

        if (Array.isArray(ownedClinics) && ownedClinics.length > 0) {
          clinicsList = ownedClinics;
        } else {
          // Fallback: read directly from localStorage
          const cached = localStorage.getItem('owned_clinics');
          if (cached && cached !== 'undefined' && cached !== 'null') {
            const parsed = JSON.parse(cached);
            if (Array.isArray(parsed) && parsed.length > 0) {
              clinicsList = parsed;
            }
          }
        }

        if (clinicsList.length > 0) {
          setClinics(clinicsList);
        }
      } catch (error) {
        console.error('Error loading owned clinics:', error);
      }
    }
  }, [isOwner, isVeterinarian, getOwnedClinics, currentClinicId]);

  // No need for separate state - selectedClinicId is derived from URL params

  // Handle clinic selection change
  const handleClinicChange = async (event) => {
    const newClinicId = event.target.value;
    const newClinicIdNum = parseInt(newClinicId);
    if (!newClinicId || newClinicIdNum === selectedClinicId || newClinicId === String(selectedClinicId)) return;

    try {
      // Find the selected clinic
      const selectedClinic = clinics.find(c => c.id === newClinicIdNum || c.id === parseInt(newClinicId));

      // Update user data with new clinic_id
      const userData = localStorage.getItem("user_data");
      if (userData && userData !== "undefined" && userData !== "null") {
        const userDataParsed = JSON.parse(userData);
        userDataParsed.clinic_id = newClinicIdNum;

        if (selectedClinic) {
          userDataParsed.clinic = {
            ...userDataParsed.clinic,
            id: selectedClinic.id,
            name: selectedClinic.name_en || selectedClinic.name,
            name_en: selectedClinic.name_en,
            name_ar: selectedClinic.name_ar,
            is_owner: true // owned_clinics are always owned
          };
        }

        localStorage.setItem("user_data", JSON.stringify(userDataParsed));
      }

      // Store clinic object
      if (selectedClinic) {
        localStorage.setItem(
          "clinic",
          JSON.stringify({
            is_owner: true, // owned_clinics are always owned
            clinic_id: selectedClinic.id
          })
        );
      }

      // Set loading state
      setIsSwitchingClinic(true);

      // Refresh clinic data to load the newly selected clinic
      refresh();

      // Update URL immediately with new clinicId, preserving current view
      // The select value will update automatically when URL changes
      const targetView = currentIndex || 'setup';
      setSelectedCard(targetView);
      navigate(`/medical/clinic/${newClinicIdNum}/${targetView}`, { replace: true });
    } catch (error) {
      console.error('Error switching clinic:', error);
    }
  };

  const cards = [
    {
      key: "setup",
      icon: "⚙️",
      labelKey: "clinicSidebar.cards.setup",
      owner: true, // Always show setup card
    },
    {
      key: "appointment-management",
      icon: "📅",
      labelKey: "clinicSidebar.cards.operations",
      always: true,
    },
    {
      key: "visit-reports",
      icon: "📊",
      labelKey: "clinicSidebar.cards.visitReports",
      owner: true,
      veterinarian: true
    },
    {
      key: "dashboard",
      icon: "📈",
      labelKey: "clinicSidebar.cards.dashboard",
      owner: true,
    },
    {
      key: "chat",
      icon: "💬",
      labelKey: "clinicSidebar.cards.chat",
      owner: true,
      veterinarian: true
    },
  ];

  // Set selected card based on index prop, but only if it's different from current
  useEffect(() => {
    if (tabType && tabType !== selectedCard) {
      const isVerified = isClinicVerified();
      // If unverified, don't allow navigation to appointment-management
      if (!isVerified) {
        setSelectedCard("setup");
        return;
      }
      if (tabType === "outpatient" || tabType === "inpatient" || tabType === "appointments") {
        setSelectedCard("appointment-management");
      }
    }
  }, [tabType, selectedCard, setSelectedCard]);
  useEffect(() => {
    if (index && index !== selectedCard) {
      const isVerified = isClinicVerified();
      // If unverified, only allow "edit" and "setup"
      if (!isVerified && index !== "edit" && index !== "setup") {
        setSelectedCard("setup");
        return;
      }
      setSelectedCard(index);
    }
  }, [index, selectedCard, setSelectedCard]);
  // Show all cards - including create-clinic for users with existing clinics
  return (
    <aside
      className={`clinicinfo-sidebar ${isCollapsed ? 'collapsed' : ''}`}
      dir={i18n.dir()}
      lang={i18n.language}
    >
      {/* Clinic Selector Dropdown */}
      {(isOwner() || isVeterinarian) && clinics.length > 0 && (
        <div className="clinicinfo-clinic-selector">
          {!isCollapsed && (
            <label className="clinicinfo-clinic-selector-label">
              {t("clinicSidebar.selectClinic")}
              {isSwitchingClinic && (
                <span style={{ marginLeft: '8px', fontSize: '12px', color: '#666' }}>
                  {t("clinicSidebar.loading")}
                </span>
              )}
            </label>
          )}

          {isCollapsed ? (
            // Icon-mode selector: Click to open modal
            <div
              className="clinicinfo-clinic-select collapsed-trigger"
              onClick={onShowClinicSelect}
              title={
                clinics.find(
                  (c) =>
                    c.id === selectedClinicId ||
                    c.id === parseInt(selectedClinicId)
                )?.name_en ||
                clinics.find(
                  (c) =>
                    c.id === selectedClinicId ||
                    c.id === parseInt(selectedClinicId)
                )?.name ||
                t("clinicSidebar.selectClinic")
              }
            >
              {/* Optional: You could display a clinic logo here if available */}
              {/* For now keeping the background image style or adding a generic icon */}
            </div>
          ) : (
            // Full-mode selector: Standard dropdown
            <select
              value={selectedClinicId || ''}
              onChange={handleClinicChange}
              disabled={isSwitchingClinic || loading}
              className="clinicinfo-clinic-select"
              style={{
                opacity: (isSwitchingClinic || loading) ? 0.6 : 1,
                cursor: (isSwitchingClinic || loading) ? 'wait' : 'pointer'
              }}
            >
              {clinics.map((clinic) => (
                <option key={clinic.id} value={clinic.id}>
                  {clinic.name_en ||
                    clinic.name ||
                    t("clinicSidebar.fallbackClinicName", { id: clinic.id })}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* Warning message for unverified clinic */}
      {hasClinicId && !isClinicVerified() && (
        <div
          className="clinicinfo-sidebar-warning"
          style={{
            margin: isCollapsed ? "8px 4px" : "12px 8px",
            padding: isCollapsed ? "8px 4px" : "12px",
            backgroundColor: "#FEF3C7",
            border: "1px solid #FCD34D",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontSize: isCollapsed ? "16px" : "14px",
            color: "#92400E",
            lineHeight: "1.4"
          }}
          title={isCollapsed ? t("clinicSidebar.verificationWarning", "Some features are disabled until your clinic is verified.") : undefined}
        >
          <span style={{ fontSize: isCollapsed ? "20px" : "18px", flexShrink: 0 }}>⚠️</span>
          {!isCollapsed && (
            <span style={{ fontWeight: "500" }}>
              {t("clinicSidebar.verificationWarning", "Some features are disabled until your clinic is verified.")}
            </span>
          )}
        </div>
      )}

      <div className="clinicinfo-sidebar-section">
        {cards
          .filter((card) => {
            if (card.always) return true;
            if (card.owner && isOwner()) return true;
            if (card.veterinarian && isVeterinarian) return true;
            return false;
          }).map((card) => {
            const isVerified = isClinicVerified();
            // Setup card is always enabled (even when unverified) because it provides access to edit
            // Other cards are disabled when unverified
            const isDisabled = !isVerified && card.key !== "setup";

            return (
              <div
                key={card.key}
                className={`clinicinfo-sidebar-card${selectedCard === card.key ||
                  (selectedCard === "create-clinic" && card.key === "setup") ||
                  (selectedCard === "team" && card.key === "setup") ||
                  (selectedCard === "manage-services" && card.key === "setup") ||
                  (selectedCard === "edit" && card.key === "setup")
                  ? " selected" : ""
                  }${isDisabled ? " disabled" : ""}`}
                onClick={() => {
                  if (!isDisabled) {
                    setIsMobileMenuOpen(false);

                    // Setup card is always accessible (even when unverified)
                    setSelectedCard(card.key);
                    const clinicId = currentClinicId || selectedClinicId;
                    if (clinicId) {
                      navigate(`/medical/clinic/${clinicId}/${card.key}`);
                    } else {
                      navigate(`/medical/clinic/${card.key}`);
                    }
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
                style={{
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  opacity: isDisabled ? 0.5 : 1,
                  pointerEvents: isDisabled ? "none" : "auto"
                }}
                title={isCollapsed ? t(card.labelKey) : (isDisabled ? t("clinicSidebar.clinicNotVerified", "Clinic must be verified to access this feature") : undefined)}
              >
                <span className="clinicinfo-sidebar-icon">{card.icon}</span>
                {!isCollapsed && (
                  <div className="clinicinfo-sidebar-label">{t(card.labelKey)}</div>
                )}
              </div>
            );
          })}
      </div>
    </aside>
  );
};

export default ClinicSidebar;
